import { useParams } from "react-router-dom";
import { useState } from "react";
import { Move, Mover } from "@/types";
import { baseApi } from "@/store/baseApi";
import { Button } from "@/components/ui/button";
import { PortalHeader } from "./PortalHeader";
import { InventoryItemForm } from "@/pages/Moves/components/InventoryTable";
import Inventory from "../EditMove/Inventory";
import { faAngleLeft } from "@awesome.me/kit-9096fb77ac/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type Response = {
  move: Move;
  mover: Mover;
};

const moveApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMoveByDigest: builder.query<Response, string>({
      query: (digest) => `/customer-hub/move/${digest}`,
    }),
    updateMoveInventory: builder.mutation<
      Move,
      { digest: string; inventory_items: InventoryItemForm[] }
    >({
      query: ({ digest, inventory_items }) => ({
        url: `/customer-hub/move/${digest}/inventory`,
        method: "PUT",
        body: { inventory_items },
      }),
    }),
  }),
});

const { useGetMoveByDigestQuery, useUpdateMoveInventoryMutation } = moveApi;

export default function ManageInventory() {
  const { digest } = useParams<{ digest: string }>();
  const { data, isLoading } = useGetMoveByDigestQuery(digest || "");
  const [updateInventory] = useUpdateMoveInventoryMutation();
  const [inventory, setInventory] = useState<InventoryItemForm[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!digest) {
      console.error("Digest is undefined");
      return;
    }

    try {
      await updateInventory({
        digest: digest,
        inventory_items: inventory,
      });

      window.location.href = `/hub/${digest}`;
    } catch (error) {
      console.error("Failed to save inventory:", error);
    }
  };

  if (isLoading || !data?.move || !data?.mover) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div
      className="min-h-screen overflow-y-auto py-12"
      style={{
        backgroundColor: data.mover.mover_configuration.brand_color_hex
          ? `#${data.mover.mover_configuration.brand_color_hex}`
          : "red",
      }}
    >
      <div className="relative container mx-auto bg-stone-50 border border-stone-300 px-8 py-8">
        <PortalHeader
          mover={data.mover}
          className="border-b border-stone-300"
        />

        <div className="flex flex-row gap-4 my-4">
          <Button
            onClick={() => (window.location.href = `/hub/move/${digest}`)}
            variant="outline"
            size="sm"
          >
            <FontAwesomeIcon icon={faAngleLeft} className="w-3 h-3" />
            Back
          </Button>
          <div className="space-x-1">
            <span className="text-xl font-bold">Inventory</span>
            <span className="text-base text-stone-800">for</span>
            <span className="text-xl font-bold">{data.move.customer_name}</span>
          </div>
        </div>

        <Inventory
          inventoryItems={data.move.inventory_items}
          moverInventoryItems={data.mover.mover_inventory_items}
          showSummary={false}
          showAsModal={false}
          customerFacing={true}
          onSetInventory={setInventory}
        />

        <div className="fixed bottom-0 left-0 right-0 z-10">
          <Button
            size="lg"
            className="w-full text-lg font-semibold"
            onClick={() => {
              setIsSaving(true);
              handleSave();
            }}
            variant="default"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
